// src/components/hollo-call-sheet.ts
import { Call, Device } from "@twilio/voice-sdk";
import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { holloCallSheetStyles } from "./hollo-call-sheet-styles";
import { getTwilioAccessToken } from "../../apis/voice-api";

/**
 * Twilio Voice via npm (@twilio/voice-sdk)
 * Props:
 *  - wsUrl: kept for backward-compat (not used now, but harmless)
 *  - to: E.164 number or client identifier passed to Twilio "connect"
 */

@customElement("hollo-call-sheet")
export class HolloCallSheet extends LitElement {
  @property() fileId = "";
  @property() alt = "";
  @property({ type: Number }) seconds = 0;

  @property({ type: String }) wsUrl = ""; // unused but kept for compat
  @property({ type: String }) to = ""; // optional dial target
  @property({ type: String }) language = "en";

  @state() private status:
    | "idle"
    | "fetching-token"
    | "ready"
    | "connecting"
    | "connected"
    | "closed"
    | "error" = "idle";
  @state() private micMuted = false;

  private device: Device | null = null;
  private activeCall: Call | null = null;
  private destroyed = false;

  // tiny delay so media/ICE are primed before connect
  private CALL_CONNECT_DELAY_MS = 300;

  connectedCallback() {
    super.connectedCallback();
    this.startCall();
  }
  disconnectedCallback() {
    this.cleanup();
    super.disconnectedCallback();
  }

  // ---- Twilio integration ----
  private async startCall() {
    try {
      // Pre-request mic for smoother UX (Twilio can also prompt on connect)
      await navigator.mediaDevices
        .getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        })
        .catch(() => {
          throw new Error("Microphone permission denied");
        });

      // 1) Get access token
      this.status = "fetching-token";
      const token = await getTwilioAccessToken();
      console.log("token", token);
      if (this.destroyed) return;

      // 2) Create Device
      this.device = new Device(token);

      // 3) Device events (SDK v2)
      this.device.on("registered", () => {
        if (this.destroyed) return;
        this.status = "ready";
        setTimeout(() => this.connectCall(), this.CALL_CONNECT_DELAY_MS);
      });

      this.device.on("error", () => {
        if (this.destroyed) return;
        this.status = "error";
      });

      this.device.on("unregistered", () => {
        if (this.destroyed) return;
        this.status = "closed";
      });

      // Token lifecycle
      this.device.on("tokenWillExpire", async () => {
        const newToken = await getTwilioAccessToken();
        await this.device?.updateToken(newToken);
      });
      this.device.on("tokenExpired", async () => {
        const newToken = await getTwilioAccessToken();
        await this.device?.updateToken(newToken);
      });

      // Auto-accept inbound (optional)
      this.device.on("incoming", (call: Call) => {
        try {
          call.accept();
          this.bindCallEvents(call);
        } catch {}
      });

      // Some environments require explicit register()
      try {
        await this.device.register();
      } catch {
        // Older backends may auto-register on construct; ignore if already registered
      }
    } catch (err) {
      console.log("error", err);
      this.status = "error";
    }
  }

  private async connectCall() {
    if (!this.device) return;
    this.status = "connecting";

    const params: Record<string, string> = {};
    if (this.to && this.to.trim()) params["To"] = this.to.trim();
    const language = this.getNormalizedLanguage();
    if (language) {
      params["language"] = language;
    }

    try {
      const call = await this.device.connect({ params });
      this.bindCallEvents(call);
      this.activeCall = call;
      this.status = "connected";
      try {
        this.dispatchEvent(new CustomEvent("call-connected"));
      } catch {}
    } catch (e) {
      this.status = "error";
    }
  }

  private bindCallEvents(call: Call) {
    call.on("accept", () => {
      // accepted/connected
      this.status = "connected";
      try {
        this.dispatchEvent(new CustomEvent("call-connected"));
      } catch {}
    });
    call.on("disconnect", () => {
      this.activeCall = null;
      this.status = "closed";
    });
    call.on("cancel", () => {
      this.activeCall = null;
      this.status = "closed";
    });
    call.on("error", () => {
      this.activeCall = null;
      this.status = "error";
    });
  }

  private getNormalizedLanguage(): string {
    const lang = this.language?.trim();
    if (!lang) return "en";
    const normalized = lang.includes("-") ? lang.split("-")[0] : lang;
    return normalized || "en";
  }

  private cleanup() {
    this.destroyed = true;

    try {
      if (this.activeCall) {
        this.activeCall.disconnect();
      }
    } catch {}

    try {
      if (this.device) {
        // v2 API
        this.device.disconnectAll?.();
        this.device.unregister?.();
        this.device.destroy?.();
      }
    } catch {}

    this.device = null;
    this.activeCall = null;
  }

  // ---- UI actions ----
  private toggleMute = () => {
    this.micMuted = !this.micMuted;
    try {
      this.activeCall?.mute(this.micMuted);
    } catch {}
  };

  private stopCall = () => this.cleanup();

  // ---- Render ----
  private formatTime(total: number) {
    const m = Math.floor(total / 60)
      .toString()
      .padStart(2, "0");
    const s = (total % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  }

  render() {
    return html`
      <div class="hollo-call-sheet">
        <div class="hollo-call-card">
          <div class="hollo-call-hero">
            <div class="hollo-hero-content">
              <h2 class="hollo-hero-name">${this.alt}</h2>
              ${this.status === "connected"
                ? html`<div class="hollo-hero-time">
                    ${this.formatTime(this.seconds)}
                  </div> `
                : null}
              <div class="hollo-hero-status">${this.statusLabel()}</div>
            </div>
            <div class="hollo-avatar-wrap">
              <hollo-profile-image
                .fileId=${this.fileId}
                .alt=${this.alt}
                .size=${160}
              ></hollo-profile-image>
            </div>
          </div>

          <div class="hollo-control-shelf">
            <div class="hollo-controls">
              <button class="hollo-control" @click=${this.toggleMute}>
                <div class="hollo-control-icon">
                  ${this.micMuted
                    ? html`<slot name="mic-slash-icon"></slot>`
                    : html`<slot name="mic-icon"></slot>`}
                </div>
                <span class="hollo-control-label"
                  >${this.micMuted ? "unmute" : "mute"}</span
                >
              </button>

              <!-- <button class="control" @click=${this.stopCall}>
                <div class="hollo-control-icon">
                  <slot name="speaker-icon"></slot>
                </div>
                <span class="hollo-control-label">Mute</span>
              </button> -->

              <button
                class="hollo-control hollo-danger"
                @click=${() => this.dispatchEvent(new CustomEvent("end-call"))}
              >
                <div class="hollo-control-icon">
                  <slot name="end-icon"></slot>
                </div>
                <span class="hollo-control-label">end</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private statusLabel() {
    switch (this.status) {
      case "fetching-token":
        return "Connecting…";
      case "ready":
        return "";
      case "connecting":
        return "Connecting…";
      case "connected":
        return "";
      case "closed":
        // notify parent so UI collapses
        this.dispatchEvent(new CustomEvent("end-call"));
        return "Disconnected";
      case "error":
        return "Error";
      default:
        return "";
    }
  }

  static styles = holloCallSheetStyles;
}

declare global {
  interface HTMLElementTagNameMap {
    "hollo-call-sheet": HolloCallSheet;
  }
}
